/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useContext, useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import { Quote } from '~/src/pricing/quote';
import {
	DragProps,
	HandleSelectFunction,
	NavigatedItem,
	TreeDataItem,
	TreePath,
	UpdatingFieldProps,
	selectionModelLeaf,
	selectionModelTree,
} from './TemplateTreeView';
import { Charge } from '~/src/pricing/quote/change';
import { Path } from '~/src/pages/bulk-design';
import { Box, ListItem } from '@mui/material';
import { Tree } from './Tree';
import { NamedLeaf, NamedTree } from '~/src/model/NamedTree';
import TreeNodeLabel from './TreeNodeLabel';
import {
	BulkDesignContext,
	BULK_DESIGN_VIEW_MODE,
} from '~/src/design/bulk/store';
import { TreeNodeInput } from './TreeNodeInput';
import { UpArrowIcon, DownArrowIcon, RightArrow } from './icons';
import { TreeNodeMenu } from './TreeNodeMenu';
import { AlertDialog } from '~/src/design/bulk/edit/alert-dialog';
import {
	Option,
	Template,
	Variable
} from '~/src/design/template';
import { columns } from './helper/utils';

interface TreeNodeProps {
	node: TreeDataItem;
	quoteData: Quote | Charge | undefined;
	updateSelectedPath: TreePath;
	path: Path | undefined;
	editMode: boolean;
	onSelect: HandleSelectFunction;
	onDelete: (path: Path) => void;
	selectedItemsData:
		| selectionModelTree<unknown>
		| selectionModelLeaf<unknown>
		| undefined;
	selectedPath: Path | undefined;
	setFieldBasedOnPath: UpdatingFieldProps;
	navigatedItem: NavigatedItem;
	onDragStart: DragProps;
	onDrop: DragProps;
	onDragOver: (e: React.DragEvent) => void;
	topParentData: NamedTree<unknown>;
	setTreeData: (e: NamedTree<unknown>) => void;
	resetNavigatedItem: () => void;
}

type TemplateValueType = (Variable | Option) & { Type: string }

export function TreeNode({
	node,
	navigatedItem,
	updateSelectedPath,
	quoteData,
	path,
	editMode,
	onSelect,
	onDelete,
	selectedItemsData,
	selectedPath,
	setFieldBasedOnPath,
	onDragStart,
	onDrop,
	onDragOver,
	topParentData,
	setTreeData,
	resetNavigatedItem,
}: TreeNodeProps) {
	const { mainViewMode } = useContext(BulkDesignContext);
	const [isNavigatedItem, setIsNavigatedItem] = useState(
		path && navigatedItem && path.toString() === navigatedItem.toString()
	);
	const isSelectedPath =
		selectedPath && selectedPath.toString() === path?.toString();
	const editModeSelected = selectedItemsData?.selected;

	const [showChildren, setShowChildren] = React.useState(false);
	const [dragEntered, setDragEntered] = useState(false);
	const [openDelete, setOpenDelete] = useState(false);

	const templateValues = useMemo<TemplateValueType[]>(() => {
		const item = (node as NamedLeaf<unknown>).Item as Template;
		if (item) {
			return [
				...(item.Opts || []).map((variable) => ({
					...variable,
					Type: 'Opts',
				})),
				...(item.Vars || []).map((variable) => ({
					...variable,
					Type: 'Vars',
				})),
			];
		}

		return [];
	}, [node]);

	const gridTemplateColumns = useMemo(() => {
		if (mainViewMode === BULK_DESIGN_VIEW_MODE.LIST_VIEW) {
			return `minmax(12px, 12px) 1fr repeat(${columns.length}, auto) 176px`;
		}
		return '12px 1fr auto';
	}, [mainViewMode]);

	const handleCountChange = (newCount: string) => {
		const newValue = parseInt(newCount, 10);
		if (!isNaN(newValue)) {
			if (path) {
				setFieldBasedOnPath(path, 'Count', newValue);
			}
		}
	};

	const handleTooltipClick = () => {
		setShowChildren(!showChildren);
	};

	const handleCheckboxClick = () => {
		if (path) {
			onSelect({ path, selectStatus: !editModeSelected || false });
		}
	};

	const onDragEnter = (): void => {
		setDragEntered(true);
	};

	const onDragLeave = (): void => {
		setDragEntered(false);
	};

	const handleMenuAction = (action: string) => {
		if (action === 'delete') {
			setOpenDelete(true);
		}
	};

	const handleDelete = (yes: boolean) => {
		if (path && yes) {
			onDelete(path as Path);
		}
		setOpenDelete(false);
	};

	useEffect(() => {
		if (navigatedItem?.toString() === path?.toString()) {
			setIsNavigatedItem(true);
		} else {
			setIsNavigatedItem(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [navigatedItem]);

	/*
	 * 'Items' in node means data type is BulkDesign
	 * 'Item' in node means data type is BulkDesignLeaf
	 */
	return (
		<>
			<ListItem
				draggable
				onDragStart={(e) => {
					if (path) {
						onDragStart(e, path);
					}
				}}
				onDrop={(e) => {
					setDragEntered(false);
					if (path) {
						onDrop(e, path);
					}
				}}
				onDragOver={onDragOver}
				onDragLeave={onDragLeave}
				onDragEnter={onDragEnter}
				data-tree-path={JSON.stringify(path)}
				data-tree-is-navigated={isNavigatedItem}
				data-tree-is-selected={isSelectedPath}
				className="tree-list-item"
				onMouseEnter={() => {
					resetNavigatedItem();
					setIsNavigatedItem(true);
				}}
				onMouseLeave={() => setIsNavigatedItem(false)}
			>
				<Box
					className={classNames('tree-node-container', {
						['tree-node-container--selected']: !editMode && isSelectedPath,
						['tree-node-container--selected-edit']:
							isNavigatedItem ||
							(!isNavigatedItem && editMode && editModeSelected),
						['tree-node-container--selected-hover']:
							!isNavigatedItem && !editMode && isSelectedPath,
						['tree-node-container--dragging']: dragEntered,
					})}
					style={{ gridTemplateColumns: gridTemplateColumns }}
				>
					{/* divider */}
					<Box className="tree-node-container__divider"></Box>
					{/* label */}
					<TreeNodeLabel
						node={node}
						path={path}
						editMode={editMode}
						showChildren={showChildren}
						editModeSelected={editModeSelected}
						setFieldBasedOnPath={setFieldBasedOnPath}
						onShowChildren={handleTooltipClick}
						onSelectChecbox={handleCheckboxClick}
						onClick={() => path && updateSelectedPath(path)}
					/>
					{/* Columns - visible only in itemized list view */}
					{mainViewMode === BULK_DESIGN_VIEW_MODE.LIST_VIEW && (
						<>
							{columns.map((column) => (
								<Box
									key={column.id}
									className="tree-node-column"
									style={{
										width: column.width,
										minWidth: column.width,
										maxWidth: column.width,
										justifyContent: column.align || 'center',
										textAlign: column.align || 'center',
									}}
								>
									{column.render
										? column.render(node, quoteData, templateValues)
										: column.label}
								</Box>
							))}
						</>
					)}
					{/* button group */}
					<Box
						className={classNames('label-right', {
							mini: mainViewMode !== BULK_DESIGN_VIEW_MODE.LIST_VIEW,
						})}
					>
						<Box component="span">
							{mainViewMode === BULK_DESIGN_VIEW_MODE.LIST_VIEW
								? ''
								: `$ ${quoteData?.Total?.USD ?? 0}`}
						</Box>
						<Box className="count-box">
							<TreeNodeInput
								className="tag"
								suffix="x"
								onChange={handleCountChange}
							>
								{node.Count}x
							</TreeNodeInput>
							<Box component="span" className="count-arrows-div">
								<Box
									component="span"
									className="count-arrows-item"
									onClick={() => {
										handleCountChange((node.Count + 1).toString());
									}}
								>
									<UpArrowIcon />
								</Box>
								<Box
									component="span"
									className="count-arrows-item"
									onClick={() => {
										node.Count > 1 &&
											handleCountChange((node.Count - 1).toString());
									}}
								>
									<DownArrowIcon />
								</Box>
							</Box>
						</Box>
						<TreeNodeMenu
							id={`tree-node-menu-${path?.join('-')}`}
							onAction={handleMenuAction}
						/>
						<Box
							style={{ width: '16px' }}
							onClick={() => path && updateSelectedPath(path)}
							className="tree-icon"
						>
							{'Item' in node && <RightArrow />}
						</Box>
					</Box>
				</Box>
				{'Items' in node && node.Items.length && showChildren ? (
					<Tree
						navigatedItem={navigatedItem}
						onDelete={onDelete}
						setFieldBasedOnPath={setFieldBasedOnPath}
						selectedPath={selectedPath}
						selectedItemsData={
							selectedItemsData
								? 'Items' in selectedItemsData
									? selectedItemsData.Items
									: undefined
								: undefined
						}
						onSelect={onSelect}
						editMode={editMode}
						path={path}
						quoteData={
							quoteData
								? 'Charges' in quoteData
									? quoteData.Charges
									: 'Items' in quoteData
										? quoteData.Items
										: undefined
								: undefined
						}
						updateSelectedPath={updateSelectedPath}
						treeData={node.Items}
						onDragStart={onDragStart}
						onDrop={onDrop}
						onDragOver={onDragOver}
						topParentData={topParentData}
						setTreeData={setTreeData}
						resetNavigatedItem={resetNavigatedItem}
					/>
				) : null}
			</ListItem>
			<AlertDialog
				open={openDelete}
				title="Are you sure you want to delete it?"
				okLabel="Yes"
				cancelLabel="No"
				onOk={handleDelete}
				onClose={() => setOpenDelete(false)}
			/>
		</>
	);
}
